import { ClipboardCheck, AlertTriangle } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  getCoreRowModel,
  useReactTable,
  VisibilityState,
  Column,
} from '@tanstack/react-table'

import {
  CheckboxFilter,
  CheckboxFilterOption,
  ClearFiltersButton,
  ColumnSelectorFilter,
  DataTableNew,
  generateSkeletonData,
  isSkeletonRow,
  ListPageHeader,
  PageSizeFilter,
  SearchFilter,
  useSkeletonColumns,
} from '@/components/newRedesign/components'
import { ListPageLayout } from '@/components/newRedesign/layouts'
import { InventoryItem } from './interfaces/inventory'
import { SimpleTablePagination } from '@/components/table/SimpleTablePagination'

import { columns } from './table/Columns'
import { TableContextProvider, useTableContext } from './table/TableContext'
import { LogisticInventoryProvider } from './hooks/useLogisticInventory'
import { ProductDetailsProvider } from './contexts/ProductDetailsContext'

import './styles/inventory.css'

// Expiration filter options
const expirationOptions: CheckboxFilterOption[] = [
  { id: 'all', label: 'Todos', value: 'urgente' },
  { id: 'urgente', label: 'Urgente', value: 'urgente' },
  { id: 'alerta', label: 'Alerta', value: 'alerta' },
  { id: 'normal', label: 'Normal', value: 'normal' },
]

function InventoryListContent() {
  return (
    <ListPageLayout>
      {/* Header with Breadcrumb */}
      <ListPageLayout.Header
        breadcrumbIcon={<ClipboardCheck className="h-4 w-4 text-gray-400" />}
        breadcrumbItems={[{ label: 'Logística' }, { label: 'Inventario' }]}
      />

      {/* Main Content */}
      <ListPageLayout.Content>
        {/* Header with filters */}
        <TableContextProvider>
          <InventoryListHeaderWrapper />

          {/* Table - Using flex-1 to occupy available space */}
          <div className="mt-4 flex flex-1 flex-col overflow-hidden">
            <InventoryTable />
          </div>
        </TableContextProvider>
      </ListPageLayout.Content>
    </ListPageLayout>
  )
}

/**
 * InventoryTable - Renders inventory with DataTableNew
 */
function InventoryTable() {
  const navigate = useNavigate()
  const {
    inventoryData,
    isLoading,
    isFetching,
    setColumns,
    currentPage,
    setCurrentPage,
    pageSize,
    refetch,
    totalItems,
    totalPages,
  } = useTableContext()

  // Manage column visibility with localStorage
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const stored = localStorage.getItem('inventory-table-columns')
      if (stored) {
        const parsed = JSON.parse(stored)
        return parsed
      }
      // All columns visible by default
      return {}
    }
  )

  const skeletonData = useMemo(() => {
    if (!isLoading && !isFetching) return []
    return generateSkeletonData<InventoryItem>(pageSize)
  }, [isLoading, isFetching, pageSize])

  const data = isLoading || isFetching ? skeletonData : inventoryData || []

  // Columns with skeleton during loading
  const tableColumns = useSkeletonColumns(columns, isLoading || isFetching)

  // Create table with TanStack
  const table = useReactTable({
    data,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: (updater) => {
      const newVisibility =
        typeof updater === 'function' ? updater(columnVisibility) : updater
      setColumnVisibility(newVisibility)
      localStorage.setItem(
        'inventory-table-columns',
        JSON.stringify(newVisibility)
      )
    },
    state: {
      columnVisibility,
    },
  })

  // Expose columns to context for selector
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allColumns = table.getAllColumns() as any
    setColumns(allColumns)
  }, [table, setColumns, columnVisibility])

  // Handle row click
  const handleRowClick = (item: InventoryItem) => {
    // Don't navigate if skeleton or loading
    if (isLoading || isFetching || isSkeletonRow(item)) return

    // Navigate to batches view for this inventory item
    navigate(`/logistic/inventory/${item.id}/lotes`)
  }

  return (
    <div className="flex h-full flex-col">
      {/* Table - flex-1 to occupy available space */}
      <div className="relative flex-1 overflow-hidden">
        {/* Loading indicator during search/filtering */}
        {isFetching && !isLoading && (
          <div className="absolute left-0 right-0 top-0 z-10 h-0.5 overflow-hidden bg-gray-100">
            <div className="h-full w-full animate-pulse bg-blue-500" />
          </div>
        )}
        <DataTableNew height="100%" onRowClick={handleRowClick} table={table} />
      </div>

      {/* Pagination - fixed height */}
      <div className="flex-shrink-0">
        <SimpleTablePagination
          currentPage={currentPage}
          isLoading={isLoading || isFetching}
          onPageChange={setCurrentPage}
          onRefresh={refetch}
          pageSize={pageSize}
          totalItems={totalItems}
          totalPages={totalPages}
        />
      </div>
    </div>
  )
}

// Wrapper component that has access to TableContext
function InventoryListHeaderWrapper() {
  const {
    pageSize,
    setPageSize,
    columns: tableColumns,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
  } = useTableContext()

  // Extract filter values
  const selectedSupplier = (filters.supplier as string) || ''
  const expirationFilters = (filters.expiration as {
    todos?: boolean
    urgente?: boolean
    alerta?: boolean
    normal?: boolean
  }) || {
    todos: true,
    urgente: false,
    alerta: false,
    normal: false,
  }

  // Handle filter changes
  const handleSupplierChange = (supplierId: string) => {
    setFilters({
      ...filters,
      supplier: supplierId,
    })
  }

  const handleExpirationChange = (values: string[]) => {
    const selectedValue = values[0] || 'all'

    if (selectedValue === 'all') {
      setFilters({
        ...filters,
        expiration: {
          todos: true,
          urgente: false,
          alerta: false,
          normal: false,
        },
      })
    } else {
      setFilters({
        ...filters,
        expiration: {
          todos: false,
          urgente: selectedValue === 'urgente',
          alerta: selectedValue === 'alerta',
          normal: selectedValue === 'normal',
        },
      })
    }
  }

  const clearAllFilters = () => {
    setSearchQuery('')
    setFilters({
      supplier: '',
      expiration: {
        todos: true,
        urgente: false,
        alerta: false,
        normal: false,
      },
    })
  }

  // Check if there are active filters
  const hasActiveFilters =
    searchQuery.trim() !== '' ||
    selectedSupplier !== '' ||
    !expirationFilters.todos

  // Current values for expiration filter
  const expirationValues: string[] = []
  if (expirationFilters.todos) {
    expirationValues.push('all')
  } else {
    if (expirationFilters.urgente) expirationValues.push('urgente')
    if (expirationFilters.alerta) expirationValues.push('alerta')
    if (expirationFilters.normal) expirationValues.push('normal')
  }

  return (
    <InventoryListHeader
      clearAllFilters={clearAllFilters}
      columns={tableColumns}
      expirationValues={expirationValues}
      handleExpirationChange={handleExpirationChange}
      handleSupplierChange={handleSupplierChange}
      hasActiveFilters={hasActiveFilters}
      pageSize={pageSize}
      searchQuery={searchQuery}
      selectedSupplier={selectedSupplier}
      setPageSize={setPageSize}
      setSearchQuery={setSearchQuery}
    />
  )
}

interface InventoryListHeaderProps {
  searchQuery: string
  setSearchQuery: (_value: string) => void
  selectedSupplier: string
  handleSupplierChange: (_supplierId: string) => void
  expirationValues: string[]
  handleExpirationChange: (_values: string[]) => void
  clearAllFilters: () => void
  hasActiveFilters: boolean
  columns: Column<InventoryItem, unknown>[]
  pageSize: number
  setPageSize: (_size: number) => void
}

function InventoryListHeader({
  searchQuery,
  setSearchQuery,
  expirationValues,
  handleExpirationChange,
  clearAllFilters,
  hasActiveFilters,
  columns,
  pageSize,
  setPageSize,
}: InventoryListHeaderProps) {
  const columnsKey = columns
    .map((col) => `${col.id}:${col.getIsVisible()}`)
    .join(',')

  return (
    <ListPageHeader
      search={
        <SearchFilter
          onChange={setSearchQuery}
          placeholder="Buscar productos..."
          value={searchQuery}
        />
      }
      secondaryActions={
        <>
          {/* Secondary buttons - Order 2 */}
          <div className="flex-none" style={{ order: 2 }}>
            <ColumnSelectorFilter columns={columns} key={columnsKey} />
          </div>

          <div className="flex-none" style={{ order: 2 }}>
            <PageSizeFilter
              onChange={setPageSize}
              options={[10, 20, 30, 40, 50]}
              pageSize={pageSize}
            />
          </div>

          {/* Filters - Order 3 */}
          <div className="flex-none" style={{ order: 3 }}>
            <CheckboxFilter
              allOptionId="all"
              exclusive
              icon={<AlertTriangle className="h-4 w-4" />}
              onChange={handleExpirationChange}
              options={expirationOptions}
              title="Vencimiento"
              value={expirationValues}
            />
          </div>

          {/* Clear All Filters Button - Order 4 (appears at the end) */}
          <div className="flex-none" style={{ order: 4 }}>
            <ClearFiltersButton
              onClick={clearAllFilters}
              visible={hasActiveFilters}
            />
          </div>
        </>
      }
    />
  )
}

export default function InventoryList() {
  return (
    <LogisticInventoryProvider>
      <ProductDetailsProvider>
        <InventoryListContent />
      </ProductDetailsProvider>
    </LogisticInventoryProvider>
  )
}
